import {
  BillGroupSummarySchema,
  GetSubscriptionSchema,
  IGetSubscriptionSchema,
} from '@monetize/types/app';
import {
  ApiListResponse,
  DEFAULT_API_RESPONSE,
  DEFAULT_PAGER,
  DEFAULT_ROWS,
  FilterType,
  TDataTablePager,
} from '@monetize/types/common';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { useGetAllBillGroups } from '../../api/billGroupService';
import { useGetListData } from '../../api/queryUtils';
import {
  buildFilterParamsRequestObject,
  sortByProductObjType,
} from '../../utils';
import { arrayToObject } from '../../utils/misc';

const SubscriptionWithBillGroup = GetSubscriptionSchema.extend({
  billGroup: BillGroupSummarySchema,
});

export type ISubscriptionWithBillGroup = z.infer<
  typeof SubscriptionWithBillGroup
>;

export const useSubscriptions = ({
  accountId,
  billGroupId,
  fetchAll,
  view = '',
}: {
  accountId: string;
  billGroupId?: string;
  fetchAll?: boolean;
  view?: string;
}) => {
  const [filters, setFilters] = useState<FilterType[]>([]);
  const [searchKey] = useState<string>('description');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pager, setPager] = useState<TDataTablePager>({
    ...DEFAULT_PAGER,
    rows: fetchAll ? 100 : DEFAULT_ROWS,
  });

  const rootFilter = useMemo(() => ({ view }), [view]);

  const filterParams = useMemo(() => {
    const params = {
      ...buildFilterParamsRequestObject(filters, searchTerm, searchKey),
      ...rootFilter,
    };
    return params;
  }, [filters, searchTerm, searchKey, rootFilter]);

  const [subscriptionsWithBillGroup, setSubscriptionsWithBillGroup] =
    useState<ApiListResponse<ISubscriptionWithBillGroup>>();

  const {
    isLoading: isBillGroupResLoading,
    isFetching: isBillGroupResFetching,
    data: billGroupsById,
  } = useGetAllBillGroups(
    { filters: { accountId } },
    {
      enabled: !!accountId,
      select: (data) => arrayToObject(data, 'id'),
    },
  );

  const {
    isLoading,
    isFetching,
    data: listData,
    isError,
    error,
    refetch,
  } = useGetListData<IGetSubscriptionSchema>(
    'accountSubscriptions',
    {
      config: pager,
      filters: billGroupId ? { ...filterParams, billGroupId } : filterParams,
    },
    {
      endpointArgs: { accountId },
      meta: { showErrorToast: true },
      select: (data) => {
        if (data.content) {
          data.content.forEach((subscription) => {
            subscription.subscriptionItems = sortByProductObjType(
              subscription.subscriptionItems,
            );
          });
        }
        return data;
      },
    },
  );

  useEffect(() => {
    if (listData && billGroupsById) {
      setSubscriptionsWithBillGroup(() => {
        return {
          ...listData,
          content: listData.content.map((subscription) => {
            return {
              ...subscription,
              billGroup: billGroupsById[subscription.billGroupId],
            };
          }),
        };
      });
    }
  }, [listData, billGroupsById]);

  const onResetFilter = () => {
    setFilters([]);
    setPager(DEFAULT_PAGER);
  };

  return {
    pager,
    setPager,
    filters,
    setFilters,
    searchKey,
    searchTerm,
    setSearchTerm,
    onResetFilter,
    totalRecords: listData?.totalElements || 0,
    loading:
      (isLoading && isFetching) ||
      (isBillGroupResLoading && isBillGroupResFetching),
    listData:
      subscriptionsWithBillGroup ||
      (DEFAULT_API_RESPONSE as ApiListResponse<ISubscriptionWithBillGroup>),
    isError,
    error,
    refetch,
  };
};
